import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/social';

const C = {
  accent:'#3AAF58', accentLight:'#EAF7EE',
  bg:'#F3F6F3', surface:'#FFFFFF',
  border:'#E2E8E2', border2:'#CDD8CD',
  text:'#1A2E1E', text2:'#3D5E44', text3:'#7A9A80',
  amber:'#D4840F', amberBg:'#FEF6E7', amberBorder:'#F0D499',
  red:'#C83333', redBg:'#FDEAEA', redBorder:'#F0B8B8',
  blue:'#2563A8', blueBg:'#EAF1FB', blueBorder:'#B8D0F0',
  purple:'#6B48A8', purpleBg:'#F0EBFB', purpleBorder:'#C8B8F0',
  teal:'#1A7A7A', tealBg:'#E7F5F5', tealBorder:'#A8D8D8',
  fb:'#1877F2', ig:'#E1306C', wa:'#25D366', google:'#4285F4',
};

const inr = n => 'Rs.' + Number(n||0).toLocaleString('en-IN', { maximumFractionDigits:0 });
const today = () => new Date().toISOString().slice(0,10);

const PLATFORMS = [
  { id:'facebook', label:'Facebook', color:C.fb },
  { id:'instagram', label:'Instagram', color:C.ig },
  { id:'whatsapp', label:'WhatsApp', color:C.wa },
  { id:'google', label:'Google Ads', color:C.google },
  { id:'linkedin', label:'LinkedIn', color:'#0A66C2' },
  { id:'manual', label:'Manual Entry', color:C.accent },
];

const platformColor = p => PLATFORMS.find(x=>x.id===p)?.color || C.accent;
const platformLabel = p => PLATFORMS.find(x=>x.id===p)?.label || p;

const Card = ({ children, style={} }) => (
  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,.05)', ...style }}>{children}</div>
);

const Btn = ({ children, onClick, variant='primary', style={}, disabled }) => {
  const v = {
    primary:{ background:C.accent, color:'#fff', border:'none' },
    danger: { background:C.red, color:'#fff', border:'none' },
    ghost:  { background:'transparent', color:C.text2, border:`1px solid ${C.border2}` },
    light:  { background:C.accentLight, color:C.accent, border:'none' },
  };
  return <button onClick={onClick} disabled={disabled}
    style={{ ...v[variant], padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600,
      cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.5:1,
      display:'inline-flex', alignItems:'center', gap:6, whiteSpace:'nowrap', ...style }}>{children}</button>;
};

const Inp = ({ label, value, onChange, type='text', opts, placeholder='' }) => {
  const base = { width:'100%', padding:'9px 12px', background:C.bg, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13, boxSizing:'border-box' };
  return (
    <div style={{ marginBottom:12 }}>
      {label && <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:5 }}>{label}</div>}
      {opts
        ? <select style={base} value={value} onChange={e=>onChange(e.target.value)}>
            {opts.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
          </select>
        : <input type={type} style={base} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} />}
    </div>
  );
};

const Modal = ({ title, onClose, children, width='520px' }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
    <Card style={{ width, maxWidth:'100%', maxHeight:'90vh', overflow:'auto', padding:28 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:17, fontWeight:800, color:C.text }}>{title}</div>
        <button onClick={onClose} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, width:32, height:32, cursor:'pointer', color:C.text2 }}>X</button>
      </div>
      {children}
    </Card>
  </div>
);

const Th = ({ c }) => <th style={{ padding:'10px 14px', fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.06em', textAlign:'left', background:'#F5F8F5', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{c}</th>;
const Td = ({ children, style={} }) => <td style={{ padding:'11px 14px', fontSize:13, borderBottom:`1px solid ${C.border}`, verticalAlign:'middle', ...style }}>{children}</td>;
const Empty = ({ msg }) => <div style={{ padding:'40px 20px', textAlign:'center', color:C.text3, fontSize:14 }}>{msg}</div>;

const Stat = ({ label, value, sub, color=C.accent, bg=C.accentLight, onClick }) => (
  <Card onClick={onClick} style={{ padding:18, cursor:onClick?'pointer':'default' }}>
    <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>{label}</div>
    <div style={{ fontSize:24, fontWeight:800, color, letterSpacing:'-.02em' }}>{value}</div>
    {sub && <div style={{ fontSize:12, color:C.text3, marginTop:3 }}>{sub}</div>}
  </Card>
);

const PlatformBadge = ({ platform }) => (
  <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:99, fontSize:11, fontWeight:700,
    color:'#fff', background:platformColor(platform) }}>
    {platformLabel(platform)}
  </span>
);

const statusColor = s => {
  const map = { new:[C.blue,C.blueBg,C.blueBorder], contacted:[C.amber,C.amberBg,C.amberBorder], converted:[C.accent,C.accentLight,'#A8D8B8'], lost:[C.red,C.redBg,C.redBorder] };
  return map[s] || [C.text3,C.border,C.border2];
};

// ── DASHBOARD ──────────────────────────────────────────────────────────────
function SocialDashboard({ setTab }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/dashboard`).then(r=>r.json()).then(setData).catch(()=>{});
  }, []);

  const totalLeads = (data?.leads||[]).reduce((s,l)=>s+parseInt(l.count||0),0);
  const newLeads = (data?.leads||[]).find(l=>l.status==='new')?.count||0;
  const converted = (data?.leads||[]).find(l=>l.status==='converted')?.count||0;

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:22 }}>
        <Stat label="Total Leads" value={totalLeads} sub="All platforms" onClick={()=>setTab('leads')} />
        <Stat label="New Leads" value={newLeads} sub="Uncontacted" color={C.blue} bg={C.blueBg} onClick={()=>setTab('leads')} />
        <Stat label="Converted" value={converted} sub="Into bookings" color={C.accent} onClick={()=>setTab('leads')} />
        <Stat label="Active Campaigns" value={data?.campaigns?.count||0} sub="Running now" color={C.purple} bg={C.purpleBg} onClick={()=>setTab('campaigns')} />
        <Stat label="Total Spent" value={inr(data?.campaigns?.total_spent||0)} sub="Ad spend" color={C.amber} bg={C.amberBg} onClick={()=>setTab('campaigns')} />
        <Stat label="Cost per Lead" value={totalLeads>0?inr((data?.campaigns?.total_spent||0)/totalLeads):'N/A'} sub="Average" color={C.teal} bg={C.tealBg} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        {/* Platform breakdown */}
        <Card style={{ padding:20 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Leads by Platform</div>
          {(data?.platforms||[]).length===0
            ? <Empty msg="No leads yet — connect your social platforms" />
            : (data?.platforms||[]).map(p => (
              <div key={p.platform} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <PlatformBadge platform={p.platform} />
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:100, height:6, background:C.border, borderRadius:99 }}>
                    <div style={{ height:6, width:`${Math.min((parseInt(p.count)/Math.max(totalLeads,1))*100,100)}%`, background:platformColor(p.platform), borderRadius:99 }} />
                  </div>
                  <span style={{ fontSize:14, fontWeight:700, color:C.text, minWidth:20 }}>{p.count}</span>
                </div>
              </div>
            ))}
        </Card>

        {/* Recent leads */}
        <Card style={{ padding:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ fontSize:15, fontWeight:800, color:C.text }}>Recent Leads</div>
            <Btn variant="light" onClick={()=>setTab('leads')} style={{ padding:'4px 12px', fontSize:12 }}>View All</Btn>
          </div>
          {(data?.recent||[]).length===0
            ? <Empty msg="No leads yet" />
            : (data?.recent||[]).map(l => (
              <div key={l.id} style={{ padding:'10px 12px', borderRadius:8, background:C.bg, marginBottom:8, borderLeft:`3px solid ${platformColor(l.platform)}` }}>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <div style={{ fontWeight:600, fontSize:13 }}>{l.name||l.phone||'Unknown'}</div>
                  <PlatformBadge platform={l.platform} />
                </div>
                <div style={{ fontSize:11, color:C.text3, marginTop:3 }}>{l.campaign_name||'-'} - {new Date(l.created_at).toLocaleDateString('en-IN')}</div>
              </div>
            ))}
        </Card>
      </div>

      {/* Webhook URLs */}
      <Card style={{ padding:20, background:'linear-gradient(135deg,#0A1A0F,#0F2A14)', border:'none' }}>
        <div style={{ fontSize:13, fontWeight:700, color:C.accent, marginBottom:12, textTransform:'uppercase', letterSpacing:'.08em' }}>Webhook URLs for Meta Business Manager</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          {[
            ['Facebook Lead Ads', 'https://api.botanix.in/api/social/webhook/facebook', 'botanix_fb_webhook_2026'],
            ['Instagram DMs', 'https://api.botanix.in/api/social/webhook/instagram', 'botanix_ig_webhook_2026'],
            ['WhatsApp Business', 'https://api.botanix.in/api/social/webhook/whatsapp', 'botanix_wa_webhook_2026'],
            ['Google Ads', 'https://api.botanix.in/api/social/webhook/google', 'N/A'],
          ].map(([platform, url, token]) => (
            <div key={platform} style={{ padding:'12px 14px', background:'rgba(58,175,88,.08)', borderRadius:10, border:'1px solid rgba(58,175,88,.2)' }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.accent, marginBottom:6 }}>{platform}</div>
              <div style={{ fontSize:11, color:'rgba(240,248,242,.7)', wordBreak:'break-all', marginBottom:4 }}>{url}</div>
              {token !== 'N/A' && <div style={{ fontSize:11, color:'rgba(240,248,242,.5)' }}>Verify Token: <span style={{ color:C.accent }}>{token}</span></div>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── LEADS ──────────────────────────────────────────────────────────────────
function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ platform:'facebook', name:'', email:'', phone:'', message:'', campaign_name:'', utm_source:'', utm_medium:'', utm_campaign:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = useCallback(() => {
    let url = `${API}/leads?`;
    if (filterPlatform) url += `platform=${filterPlatform}&`;
    if (filterStatus) url += `status=${filterStatus}&`;
    if (search) url += `search=${search}`;
    fetch(url).then(r=>r.json()).then(d=>setLeads(Array.isArray(d)?d:[])).catch(()=>{});
  }, [filterPlatform, filterStatus, search]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    await fetch(`${API}/leads`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const updateStatus = async (id, status) => {
    await fetch(`${API}/leads/${id}`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ status }) });
    load();
  };

  const toCRM = async (id) => {
    await fetch(`${API}/leads/${id}/to-crm`, { method:'POST' });
    load();
  };

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Social Media Leads ({leads.length})</div>
        <div style={{ display:'flex', gap:10 }}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, phone..."
            style={{ padding:'7px 12px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13, width:160 }} />
          <select value={filterPlatform} onChange={e=>setFilterPlatform(e.target.value)}
            style={{ padding:'7px 12px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13 }}>
            <option value="">All Platforms</option>
            {PLATFORMS.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
            style={{ padding:'7px 12px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13 }}>
            <option value="">All Status</option>
            {['new','contacted','converted','lost'].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <Btn onClick={()=>setShowModal(true)}>+ Add Lead</Btn>
        </div>
      </div>

      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Platform" /><Th c="Name" /><Th c="Phone" /><Th c="Email" /><Th c="Campaign" /><Th c="Message" /><Th c="Date" /><Th c="Status" /><Th c="Actions" /></tr></thead>
            <tbody>
              {leads.length===0 ? <tr><td colSpan={9}><Empty msg="No leads yet" /></td></tr>
                : leads.map(l => {
                  const [col,bg,brd] = statusColor(l.status);
                  return (
                    <tr key={l.id} style={{ background:l.status==='new'?'#F0F8FF':'transparent' }}>
                      <Td><PlatformBadge platform={l.platform} /></Td>
                      <Td><span style={{ fontWeight:600 }}>{l.name||'-'}</span></Td>
                      <Td style={{ color:C.text3 }}>{l.phone||'-'}</Td>
                      <Td style={{ color:C.text3, fontSize:12 }}>{l.email||'-'}</Td>
                      <Td style={{ fontSize:12, color:C.text3 }}>{l.campaign_name||'-'}</Td>
                      <Td style={{ fontSize:12, color:C.text3, maxWidth:150 }}>{l.message?.slice(0,50)||'-'}</Td>
                      <Td style={{ fontSize:12, color:C.text3 }}>{new Date(l.created_at).toLocaleDateString('en-IN')}</Td>
                      <Td>
                        <select value={l.status} onChange={e=>updateStatus(l.id,e.target.value)}
                          style={{ padding:'4px 8px', background:bg, border:`1px solid ${brd}`, borderRadius:6, color:col, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                          {['new','contacted','converted','lost'].map(s=><option key={s} value={s}>{s}</option>)}
                        </select>
                      </Td>
                      <Td>
                        <Btn variant="light" onClick={()=>toCRM(l.id)} style={{ padding:'4px 10px', fontSize:11 }}>
                          Push to CRM
                        </Btn>
                      </Td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      {showModal && (
        <Modal title="Add Lead Manually" onClose={()=>setShowModal(false)}>
          <Inp label="Platform" value={form.platform} onChange={f('platform')} opts={PLATFORMS.map(p=>({value:p.id,label:p.label}))} />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Name" value={form.name} onChange={f('name')} placeholder="Lead name" />
            <Inp label="Phone" value={form.phone} onChange={f('phone')} placeholder="Mobile number" />
          </div>
          <Inp label="Email" value={form.email} onChange={f('email')} placeholder="Email address" />
          <Inp label="Campaign Name" value={form.campaign_name} onChange={f('campaign_name')} placeholder="e.g. Wedding Season FB Campaign" />
          <Inp label="Message / Enquiry" value={form.message} onChange={f('message')} placeholder="What are they enquiring about?" />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <Inp label="UTM Source" value={form.utm_source} onChange={f('utm_source')} placeholder="facebook" />
            <Inp label="UTM Medium" value={form.utm_medium} onChange={f('utm_medium')} placeholder="cpc" />
            <Inp label="UTM Campaign" value={form.utm_campaign} onChange={f('utm_campaign')} placeholder="wedding_2026" />
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Save Lead</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── CAMPAIGNS ──────────────────────────────────────────────────────────────
function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(null);
  const [form, setForm] = useState({ platform:'facebook', campaign_name:'', budget:'', start_date:today(), end_date:'' });
  const [updateForm, setUpdateForm] = useState({ spent:'', impressions:'', clicks:'', leads:'', conversions:'', status:'active' });
  const f = k => v => setForm(p=>({...p,[k]:v}));
  const uf = k => v => setUpdateForm(p=>({...p,[k]:v}));

  const load = () => { fetch(`${API}/campaigns`).then(r=>r.json()).then(d=>setCampaigns(Array.isArray(d)?d:[])).catch(()=>{}); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await fetch(`${API}/campaigns`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const update = async () => {
    await fetch(`${API}/campaigns/${updateModal.id}`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify(updateForm) });
    setUpdateModal(null); load();
  };

  const totalBudget = campaigns.reduce((s,c)=>s+parseFloat(c.budget||0),0);
  const totalSpent = campaigns.reduce((s,c)=>s+parseFloat(c.spent||0),0);
  const totalLeads = campaigns.reduce((s,c)=>s+parseInt(c.leads||0),0);

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Campaign Manager</div>
        <Btn onClick={()=>setShowModal(true)}>+ New Campaign</Btn>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:18 }}>
        <Stat label="Total Campaigns" value={campaigns.length} sub="All time" />
        <Stat label="Total Budget" value={inr(totalBudget)} sub="Allocated" color={C.blue} bg={C.blueBg} />
        <Stat label="Total Spent" value={inr(totalSpent)} sub="Ad spend" color={C.amber} bg={C.amberBg} />
        <Stat label="Total Leads" value={totalLeads} sub="From campaigns" color={C.accent} />
      </div>

      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Platform" /><Th c="Campaign" /><Th c="Budget" /><Th c="Spent" /><Th c="Impressions" /><Th c="Clicks" /><Th c="Leads" /><Th c="CTR" /><Th c="CPL" /><Th c="Status" /><Th c="Action" /></tr></thead>
            <tbody>
              {campaigns.length===0 ? <tr><td colSpan={11}><Empty msg="No campaigns yet" /></td></tr>
                : campaigns.map(c => {
                  const ctr = c.impressions>0?((c.clicks/c.impressions)*100).toFixed(2):0;
                  const cpl = c.leads>0?(c.spent/c.leads).toFixed(0):0;
                  return (
                    <tr key={c.id}>
                      <Td><PlatformBadge platform={c.platform} /></Td>
                      <Td><div style={{ fontWeight:600 }}>{c.campaign_name}</div></Td>
                      <Td>{inr(c.budget)}</Td>
                      <Td style={{ color:parseFloat(c.spent)>parseFloat(c.budget)?C.red:C.text }}>{inr(c.spent)}</Td>
                      <Td style={{ color:C.text3 }}>{parseInt(c.impressions||0).toLocaleString()}</Td>
                      <Td style={{ color:C.text3 }}>{parseInt(c.clicks||0).toLocaleString()}</Td>
                      <Td><span style={{ fontWeight:700, color:C.accent }}>{c.leads}</span></Td>
                      <Td style={{ color:C.text3 }}>{ctr}%</Td>
                      <Td style={{ color:C.text3 }}>{cpl?inr(cpl):'-'}</Td>
                      <Td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:99, background:c.status==='active'?C.accentLight:C.redBg, color:c.status==='active'?C.accent:C.red, fontWeight:600 }}>{c.status}</span></Td>
                      <Td><Btn variant="light" onClick={()=>{ setUpdateForm({spent:c.spent,impressions:c.impressions,clicks:c.clicks,leads:c.leads,conversions:c.conversions,status:c.status}); setUpdateModal(c); }} style={{ padding:'4px 12px', fontSize:12 }}>Update</Btn></Td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      {showModal && (
        <Modal title="New Campaign" onClose={()=>setShowModal(false)}>
          <Inp label="Platform" value={form.platform} onChange={f('platform')} opts={PLATFORMS.map(p=>({value:p.id,label:p.label}))} />
          <Inp label="Campaign Name" value={form.campaign_name} onChange={f('campaign_name')} placeholder="e.g. Wedding Season 2026 - Facebook" />
          <Inp label="Budget (Rs)" value={form.budget} onChange={f('budget')} type="number" placeholder="Total ad budget" />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Start Date" value={form.start_date} onChange={f('start_date')} type="date" />
            <Inp label="End Date" value={form.end_date} onChange={f('end_date')} type="date" />
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Create</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {updateModal && (
        <Modal title={`Update - ${updateModal.campaign_name}`} onClose={()=>setUpdateModal(null)} width="420px">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Spent (Rs)" value={updateForm.spent} onChange={uf('spent')} type="number" />
            <Inp label="Impressions" value={updateForm.impressions} onChange={uf('impressions')} type="number" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Clicks" value={updateForm.clicks} onChange={uf('clicks')} type="number" />
            <Inp label="Leads Generated" value={updateForm.leads} onChange={uf('leads')} type="number" />
          </div>
          <Inp label="Status" value={updateForm.status} onChange={uf('status')} opts={['active','paused','completed']} />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={update} style={{ flex:1, justifyContent:'center' }}>Save</Btn>
            <Btn variant="ghost" onClick={()=>setUpdateModal(null)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── WEBHOOK SETUP ──────────────────────────────────────────────────────────
function WebhookTab() {
  const WEBHOOKS = [
    {
      platform: 'Facebook Lead Ads',
      color: C.fb,
      url: 'https://api.botanix.in/api/social/webhook/facebook',
      verify_token: 'botanix_fb_webhook_2026',
      steps: [
        'Go to developers.facebook.com → Your App → Webhooks',
        'Click "Subscribe to this object" for Page',
        'Enter Callback URL and Verify Token above',
        'Subscribe to "leadgen" field',
        'Go to your Lead Ads form and connect the webhook',
      ],
    },
    {
      platform: 'Instagram',
      color: C.ig,
      url: 'https://api.botanix.in/api/social/webhook/instagram',
      verify_token: 'botanix_ig_webhook_2026',
      steps: [
        'Go to developers.facebook.com → Your App → Webhooks',
        'Select Instagram as the object',
        'Enter Callback URL and Verify Token above',
        'Subscribe to messages field',
        'Connect your Instagram Business account',
      ],
    },
    {
      platform: 'WhatsApp Business',
      color: C.wa,
      url: 'https://api.botanix.in/api/social/webhook/whatsapp',
      verify_token: 'botanix_wa_webhook_2026',
      steps: [
        'Go to developers.facebook.com → Your App → WhatsApp → Configuration',
        'Enter Callback URL and Verify Token above',
        'Click Verify and Save',
        'Subscribe to messages webhook field',
        'Test by sending a WhatsApp message to your business number',
      ],
    },
    {
      platform: 'Google Ads',
      color: C.google,
      url: 'https://api.botanix.in/api/social/webhook/google',
      verify_token: 'N/A',
      steps: [
        'Use the Google Ads Lead Form Extension',
        'Set webhook URL in your lead form settings',
        'Map fields: name, email, phone, campaign',
        'Test with a sample submission',
        'Leads will appear in the Leads tab automatically',
      ],
    },
  ];

  const [copied, setCopied] = useState('');

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(()=>setCopied(''), 2000);
  };

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ fontSize:20, fontWeight:800, color:C.text, marginBottom:8 }}>Webhook Setup Guide</div>
      <div style={{ fontSize:13, color:C.text3, marginBottom:20 }}>Follow these steps to connect each platform to automatically receive leads.</div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(420px,1fr))', gap:16 }}>
        {WEBHOOKS.map(wh => (
          <Card key={wh.platform} style={{ padding:20, borderTop:`4px solid ${wh.color}` }}>
            <div style={{ fontSize:16, fontWeight:800, color:C.text, marginBottom:14 }}>{wh.platform}</div>

            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', marginBottom:6 }}>Callback URL</div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <div style={{ flex:1, padding:'8px 12px', background:C.bg, borderRadius:8, fontSize:12, fontFamily:'monospace', color:C.text2, wordBreak:'break-all' }}>{wh.url}</div>
                <Btn variant="light" onClick={()=>copy(wh.url,`url_${wh.platform}`)} style={{ padding:'6px 12px', fontSize:12, flexShrink:0 }}>
                  {copied===`url_${wh.platform}`?'Copied!':'Copy'}
                </Btn>
              </div>
            </div>

            {wh.verify_token !== 'N/A' && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', marginBottom:6 }}>Verify Token</div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <div style={{ flex:1, padding:'8px 12px', background:C.bg, borderRadius:8, fontSize:12, fontFamily:'monospace', color:C.accent }}>{wh.verify_token}</div>
                  <Btn variant="light" onClick={()=>copy(wh.verify_token,`tok_${wh.platform}`)} style={{ padding:'6px 12px', fontSize:12, flexShrink:0 }}>
                    {copied===`tok_${wh.platform}`?'Copied!':'Copy'}
                  </Btn>
                </div>
              </div>
            )}

            <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', marginBottom:8 }}>Setup Steps</div>
            {wh.steps.map((step,i) => (
              <div key={i} style={{ display:'flex', gap:10, marginBottom:8 }}>
                <div style={{ width:20, height:20, borderRadius:'50%', background:wh.color, color:'#fff', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{i+1}</div>
                <div style={{ fontSize:12, color:C.text2, lineHeight:1.5 }}>{step}</div>
              </div>
            ))}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── UTM BUILDER ────────────────────────────────────────────────────────────
function UTMBuilder() {
  const [form, setForm] = useState({ url:'https://botanix.in', source:'facebook', medium:'cpc', campaign:'', content:'', term:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));
  const [copied, setCopied] = useState(false);

  const utmUrl = () => {
    const params = new URLSearchParams();
    if (form.source) params.set('utm_source', form.source);
    if (form.medium) params.set('utm_medium', form.medium);
    if (form.campaign) params.set('utm_campaign', form.campaign);
    if (form.content) params.set('utm_content', form.content);
    if (form.term) params.set('utm_term', form.term);
    return `${form.url}?${params.toString()}`;
  };

  const copy = () => {
    navigator.clipboard.writeText(utmUrl());
    setCopied(true);
    setTimeout(()=>setCopied(false), 2000);
  };

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ fontSize:20, fontWeight:800, color:C.text, marginBottom:8 }}>UTM Link Builder</div>
      <div style={{ fontSize:13, color:C.text3, marginBottom:20 }}>Build trackable URLs for your campaigns to automatically identify lead sources.</div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <Card style={{ padding:24 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:16 }}>Build Your UTM Link</div>
          <Inp label="Website URL" value={form.url} onChange={f('url')} placeholder="https://botanix.in" />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Source" value={form.source} onChange={f('source')} opts={['facebook','instagram','google','whatsapp','linkedin','email','sms','other']} />
            <Inp label="Medium" value={form.medium} onChange={f('medium')} opts={['cpc','organic','social','email','referral','direct']} />
          </div>
          <Inp label="Campaign Name" value={form.campaign} onChange={f('campaign')} placeholder="e.g. wedding_season_2026" />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Content (optional)" value={form.content} onChange={f('content')} placeholder="e.g. banner_ad_1" />
            <Inp label="Term (optional)" value={form.term} onChange={f('term')} placeholder="e.g. resort+near+gurgaon" />
          </div>
        </Card>

        <Card style={{ padding:24 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:16 }}>Your Tracking URL</div>
          <div style={{ padding:'16px', background:C.bg, borderRadius:10, marginBottom:16, wordBreak:'break-all', fontSize:13, color:C.text2, lineHeight:1.7, minHeight:100 }}>
            {utmUrl()}
          </div>
          <Btn onClick={copy} style={{ width:'100%', justifyContent:'center', padding:'12px' }}>
            {copied ? 'Copied!' : 'Copy URL'}
          </Btn>

          <div style={{ marginTop:20 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:12 }}>Quick Templates</div>
            {[
              ['Facebook Wedding Campaign', 'facebook', 'cpc', 'wedding_2026'],
              ['Instagram Organic', 'instagram', 'organic', 'resort_life'],
              ['Google Ads', 'google', 'cpc', 'luxury_resort_gurgaon'],
              ['WhatsApp Link', 'whatsapp', 'social', 'wa_enquiry'],
            ].map(([label, src, med, camp]) => (
              <button key={label} onClick={()=>setForm(p=>({...p, source:src, medium:med, campaign:camp}))}
                style={{ display:'block', width:'100%', textAlign:'left', padding:'8px 12px', marginBottom:6,
                  background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, cursor:'pointer', fontSize:12, color:C.text2 }}>
                {label}
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ── ANALYTICS ──────────────────────────────────────────────────────────────
function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { fetch(`${API}/analytics`).then(r=>r.json()).then(setData).catch(()=>{}); }, []);

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ fontSize:20, fontWeight:800, color:C.text, marginBottom:20 }}>Social Media Analytics</div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <Card style={{ padding:20 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Leads by Platform</div>
          {(data?.by_platform||[]).length===0 ? <Empty msg="No data yet" />
            : (data?.by_platform||[]).map(p => {
              const convRate = p.leads>0?Math.round((p.converted/p.leads)*100):0;
              return (
                <div key={p.platform} style={{ marginBottom:14 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                    <PlatformBadge platform={p.platform} />
                    <div style={{ fontSize:12, color:C.text3 }}>{p.leads} leads | {convRate}% converted</div>
                  </div>
                  <div style={{ height:6, background:C.border, borderRadius:99 }}>
                    <div style={{ height:6, width:`${convRate}%`, background:platformColor(p.platform), borderRadius:99 }} />
                  </div>
                </div>
              );
            })}
        </Card>

        <Card style={{ padding:20 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Lead Status Breakdown</div>
          {(data?.by_status||[]).length===0 ? <Empty msg="No data yet" />
            : (data?.by_status||[]).map(s => {
              const [col,bg,brd] = statusColor(s.status);
              return (
                <div key={s.status} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <span style={{ fontSize:13, padding:'3px 12px', borderRadius:99, background:bg, color:col, border:`1px solid ${brd}`, fontWeight:600 }}>{s.status}</span>
                  <span style={{ fontSize:16, fontWeight:800, color:col }}>{s.count}</span>
                </div>
              );
            })}
        </Card>
      </div>

      <Card style={{ padding:20 }}>
        <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Top Campaigns by Leads</div>
        {(data?.top_campaigns||[]).length===0 ? <Empty msg="No campaign data yet" />
          : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr><Th c="Platform" /><Th c="Campaign" /><Th c="Leads" /><Th c="Clicks" /><Th c="Spent" /><Th c="Cost/Lead" /></tr></thead>
                <tbody>
                  {(data?.top_campaigns||[]).map((c,i) => (
                    <tr key={i}>
                      <Td><PlatformBadge platform={c.platform} /></Td>
                      <Td><span style={{ fontWeight:600 }}>{c.campaign_name}</span></Td>
                      <Td><span style={{ fontWeight:700, color:C.accent }}>{c.leads}</span></Td>
                      <Td style={{ color:C.text3 }}>{c.clicks}</Td>
                      <Td>{inr(c.spent)}</Td>
                      <Td style={{ color:C.amber, fontWeight:600 }}>{c.cost_per_lead>0?inr(c.cost_per_lead):'-'}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────
export default function SocialMediaIntegration() {
  const [tab, setTab] = useState('dashboard');

  const tabs = [
    ['dashboard','Dashboard'],['leads','Leads'],['campaigns','Campaigns'],
    ['webhook','Webhook Setup'],['utm','UTM Builder'],['analytics','Analytics'],
  ];

  return (
    <div style={{ background:C.bg, minHeight:'100vh' }}>
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:'0 28px', position:'sticky', top:0, zIndex:50 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', height:60 }}>
          <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Social Media & Lead Management</div>
          <div style={{ fontSize:12, color:C.text3 }}>Facebook · Instagram · WhatsApp · Google</div>
        </div>
        <div style={{ display:'flex', gap:2, overflowX:'auto' }}>
          {tabs.map(([id, label]) => (
            <button key={id} onClick={()=>setTab(id)}
              style={{ padding:'10px 16px', border:'none', background:'transparent',
                color: tab===id?C.accent:C.text3, fontSize:13, fontWeight:tab===id?700:500,
                cursor:'pointer', whiteSpace:'nowrap',
                borderBottom: tab===id?`2px solid ${C.accent}`:'2px solid transparent' }}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {tab==='dashboard'  && <SocialDashboard setTab={setTab} />}
      {tab==='leads'      && <LeadsTab />}
      {tab==='campaigns'  && <CampaignsTab />}
      {tab==='webhook'    && <WebhookTab />}
      {tab==='utm'        && <UTMBuilder />}
      {tab==='analytics'  && <AnalyticsTab />}
    </div>
  );
}
